import { send } from "@utils/fetch";
import type { ExperimentMappingProto_Status } from "@noom/noom-contracts/noom_contracts/backend/experiments/common/experiment_mapping";
import type { ExperimentNameAndVariationProto } from "@noom/noom-contracts/noom_contracts/backend/experiments/common/experiment_name_and_variation";
import type { GetAllExperimentMappingsForUserResponseProto } from "@noom/noom-contracts/noom_contracts/backend/experiments/experiment_runtime_service";
import { appConfig } from "src/config";
import { trackTask } from "src/utils/monitoring/events/tasks";
import { isDevModEnabled } from "src/utils/runtimeFlags";

const { COACH_SERVER_URL } = appConfig;

const ACTIVE_STATUS: ExperimentMappingProto_Status = "ACTIVE";

type AssignExperimentResponse = {
  accessCode: string;
  experiment?: ExperimentNameAndVariationProto;
};

/**
 * Fetches experiment mappings for the user from Coach Server (v1 contracts-backed
 * endpoint). Filters to ACTIVE mappings only via the status query param.
 */
export function getActiveExperimentMappings(
  accessCode: string
): Promise<GetAllExperimentMappingsForUserResponseProto> {
  return send<GetAllExperimentMappingsForUserResponseProto>(
    "GET",
    `${COACH_SERVER_URL}/experiments/v1/users/${accessCode}/experimentMappings`,
    undefined,
    { params: new URLSearchParams({ status: ACTIVE_STATUS }) }
  );
}

/**
 * Assigns the user to the named experiment via Coach Server.
 * ECT determines the variation. Throws on 4xx (not eligible or already
 * assigned in production).
 */
export function assignExperiment(
  accessCode: string,
  experimentName: string
): Promise<AssignExperimentResponse> {
  return send<AssignExperimentResponse>(
    "POST",
    `${COACH_SERVER_URL}/experiments/v1/users/${accessCode}:assignExperiment`,
    { experimentName }
  );
}

/**
 * Idempotent get-or-assign: checks existing mappings first, then assigns if
 * the user has no current mapping for the experiment.
 *
 * Returns the variation name on success, or `null` when the user is not
 * eligible (targeting miss, experiment inactive, network failure, etc.).
 *
 * In dev mode (`?idm=true`), a URL param matching `experimentName` bypasses
 * Coach Server entirely — e.g. `?sx1467=variation&idm=true` forces the
 * "variation" arm without a network round trip.
 *
 * Never throws. `trackTask` reports failures to Sentry and emits task events
 * for observability into assignment success rate.
 */
export async function getOrAssignExperiment(
  accessCode: string,
  experimentName: string
): Promise<string | null> {
  if (isDevModEnabled()) {
    const override = new URLSearchParams(window.location.search).get(
      experimentName
    );
    if (override) return override;
  }

  const task = trackTask("GetOrAssignExperiment", { experimentName });
  task.start();

  try {
    const mappings = await getActiveExperimentMappings(accessCode);
    const existing = mappings.result?.find(
      (m) => m.experiment?.name === experimentName
    );

    if (existing) {
      const variation = existing.experiment?.variation ?? null;
      task.success({ variation, source: "existing" });
      return variation;
    }

    const response = await assignExperiment(accessCode, experimentName);
    const variation = response.experiment?.variation ?? null;
    task.success({ variation, source: "new" });
    return variation;
  } catch (err) {
    task.failed(err);
    return null;
  }
}

export async function getExperimentVariation(
  accessCode: string,
  experimentName: string
): Promise<string | null> {
  try {
    const mappings = await getActiveExperimentMappings(accessCode);
    const mapping = mappings.result?.find(
      (m) => m.experiment?.name === experimentName
    );

    return mapping?.experiment?.variation;
  } catch (err) {
    return null;
  }
}

export async function isUserInExperimentVariation(
  accessCode: string,
  experimentName: string,
  variationName: string
): Promise<boolean> {
  const variation = await getExperimentVariation(accessCode, experimentName);

  return variation === variationName;
}
